'use client';
import React, { useState } from 'react';
import { submitContactMessage } from '@/lib/api';
import { siteConfig } from '../../../../config/site';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

export default function ContactForm() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [errors, setErrors] = useState<Partial<typeof form>>({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error' | 'notConnected'>('idle');

  const set = (key: keyof typeof form, value: string) => {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => ({ ...p, [key]: undefined }));
  };

  const validate = () => {
    const e: Partial<typeof form> = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    if (!form.phone.trim()) e.phone = 'Phone number is required.';
    if (!form.message.trim()) e.message = 'Message is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    const res = await submitContactMessage(form);
    setSubmitting(false);
    if (res.notConnected) setStatus('notConnected');
    else if (res.success) setStatus('success');
    else setStatus('error');
  };

  const inputClass = "w-full px-3.5 py-2.5 rounded-xl text-sm transition-all focus:outline-none";
  const inputStyle = { backgroundColor: 'var(--surface-2)', border: '1px solid var(--border-color)', color: 'var(--text)' };
  const errorStyle = { color: '#ef4444', fontSize: '12px', marginTop: '4px' };

  if (status === 'success') {
    return (
      <div className="text-center py-8 space-y-3">
        <CheckCircleIcon className="w-12 h-12 mx-auto" style={{ color: 'var(--brand)' }} />
        <p className="font-semibold" style={{ color: 'var(--text)' }}>Message sent!</p>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>We'll be in touch soon.</p>
      </div>
    );
  }

  if (status === 'notConnected') {
    return (
      <div className="text-center py-8 space-y-4">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Online messaging is not yet connected. Please call or email us directly.
        </p>
        <div className="flex flex-col gap-2">
          <a href={siteConfig.phone.tel} className="py-2.5 rounded-xl text-sm font-bold text-center transition-all hover:opacity-90" style={{ backgroundColor: 'var(--brand)', color: 'var(--bg)' }}>
            Call {siteConfig.phone.display}
          </a>
          <a href={`mailto:${siteConfig.email.support}`} className="py-2.5 rounded-xl text-sm font-semibold text-center border transition-all hover:opacity-80" style={{ borderColor: 'var(--border-color)', color: 'var(--text)' }}>
            {siteConfig.email.support}
          </a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div>
        <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Name *</label>
        <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Your name" className={inputClass} style={inputStyle} />
        {errors.name && <p style={errorStyle}>{errors.name}</p>}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Phone *</label>
          <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+1 (555) 000-0000" className={inputClass} style={inputStyle} />
          {errors.phone && <p style={errorStyle}>{errors.phone}</p>}
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Email (optional)</label>
          <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" className={inputClass} style={inputStyle} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>Message *</label>
        <textarea rows={4} value={form.message} onChange={(e) => set('message', e.target.value)} placeholder="How can we help?" className={`${inputClass} resize-none`} style={inputStyle} />
        {errors.message && <p style={errorStyle}>{errors.message}</p>}
      </div>
      {status === 'error' && (
        <p className="text-sm" style={{ color: '#ef4444' }}>Something went wrong. Please call us directly.</p>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="w-full py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
        style={{ backgroundColor: 'var(--brand)', color: 'var(--bg)' }}
      >
        {submitting ? 'Sending...' : 'Send Message'}
      </button>
    </form>
  );
}